import { useState } from 'react'

import { api } from '../api'
import { datum } from '../format'
import { useAktion, useLaden } from '../hooks'
import type { Artikel, Kopplung } from '../typen'
import ArtikelAuswahl from './ArtikelAuswahl'
import Meldungen from './Meldungen'

/**
 * Welches Material welcher Fertigartikel zieht - seit dem 02.11.2026 mit Gültigkeit („ab 01.11. zieht 10535 den
 * Stangenwickler weiß", „10780 nutzt den Karton nur bis Jahresende"; Frau Barth: die Zuordnung „wird im Laufe des
 * Jahres angepasst"). Gerechnet wird nur im Zeitraum; leer heißt offen. Bisher ging das nur über eine Regel.
 *
 * Mit `artikel` nur die Kopplungen dieses Artikels (im Formular der Stammdaten), sonst alle.
 */

interface Eigenschaften {
  /** nur die Kopplungen dieses Artikels; er steht im Formular fest */
  artikel?: Artikel | null
  /** alle Artikel - für die Auswahl von Material und Fertigartikel */
  alleArtikel: Artikel[]
}

/** „ab 01.11.2026 bis 31.12.2026", „immer" - die Gültigkeit in Worten. */
function zeitraumText(k: Pick<Kopplung, 'gueltig_ab' | 'gueltig_bis'>): string {
  if (!k.gueltig_ab && !k.gueltig_bis) return 'immer'
  return [k.gueltig_ab && `ab ${datum(k.gueltig_ab)}`, k.gueltig_bis && `bis ${datum(k.gueltig_bis)}`].filter(Boolean).join(' ')
}

export default function Kopplungen({ artikel, alleArtikel }: Eigenschaften) {
  const filter = artikel ? (artikel.art === 'material' ? { material: artikel.artikelnummer } : { fertigartikel: artikel.artikelnummer }) : {}
  const { daten, fehler: ladeFehler, neuLaden } = useLaden(() => api.kopplungen(filter), [artikel?.artikelnummer, artikel?.art])
  const { fehler, erfolg, arbeitet, setFehler, setErfolg, ausfuehren } = useAktion()
  const materialien = alleArtikel.filter((a) => a.art === 'material' && a.aktiv)
  const fertigartikel = alleArtikel.filter((a) => a.art === 'fertigartikel' && a.aktiv)
  const [neuMaterial, setNeuMaterial] = useState('')
  const [neuFertig, setNeuFertig] = useState('')
  const [neuAb, setNeuAb] = useState('')
  const [neuBis, setNeuBis] = useState('')
  // Die Zeile, deren Gültigkeit gerade geändert wird, mit den Feldern als Text.
  const [bearbeitet, setBearbeitet] = useState<{ id: number; ab: string; bis: string } | null>(null)

  const kopplungen = daten ?? []
  const material = artikel?.art === 'material' ? artikel.artikelnummer : neuMaterial
  const fertig = artikel?.art === 'fertigartikel' ? artikel.artikelnummer : neuFertig

  function zeitraumFehler(ab: string, bis: string): boolean {
    if (ab && bis && bis < ab) {
      setFehler(`„gültig bis“ (${datum(bis)}) liegt vor „gültig ab“ (${datum(ab)}).`)
      return true
    }
    return false
  }

  function anlegen() {
    if (!material || !fertig) {
      setFehler('Material und Fertigartikel wählen.')
      return
    }
    if (zeitraumFehler(neuAb, neuBis)) return
    // Ist das Paar schon gekoppelt, setzt dieselbe Anfrage nur die Gültigkeit - das sagt die Meldung.
    const vorhanden = kopplungen.some((k) => k.material === material && k.fertigartikel === fertig)
    void ausfuehren(async () => {
      const k = await api.kopplungSetzen({ material, fertigartikel: fertig, gueltig_ab: neuAb || null, gueltig_bis: neuBis || null })
      setErfolg(
        `${k.fertigartikel} zieht ${k.material} ${zeitraumText(k)}${vorhanden ? ' - die Kopplung bestand schon, die Gültigkeit ist jetzt gesetzt' : ''}.`,
      )
      setNeuAb('')
      setNeuBis('')
      neuLaden()
    })
  }

  function aendern() {
    if (!bearbeitet || zeitraumFehler(bearbeitet.ab, bearbeitet.bis)) return
    const { id, ab, bis } = bearbeitet
    void ausfuehren(async () => {
      const k = await api.kopplungAendern(id, { gueltig_ab: ab || null, gueltig_bis: bis || null })
      setErfolg(`${k.fertigartikel} → ${k.material}: gilt jetzt ${zeitraumText(k)}.`)
      setBearbeitet(null)
      neuLaden()
    })
  }

  function loeschen(k: Kopplung) {
    if (
      !window.confirm(
        `Kopplung ${k.fertigartikel} → ${k.material} entfernen? Der Fertigartikel zieht das Material dann nicht mehr, auch rückwirkend. ` +
          'Soll sie nur ab einem Tag enden, statt dessen „gültig bis“ setzen.' +
          (k.hergeleitet ? ' Sie stammt aus einer Regel und kommt mit der Regel wieder.' : ''),
      )
    )
      return
    void ausfuehren(async () => {
      await api.kopplungLoeschen(k.id)
      setErfolg(`Kopplung ${k.fertigartikel} → ${k.material} entfernt.`)
      neuLaden()
    })
  }

  return (
    <div>
      <Meldungen fehler={ladeFehler ?? fehler} erfolg={erfolg} />
      {kopplungen.length === 0 ? (
        <p className="hinweis">{daten ? 'Keine Kopplung.' : ''}</p>
      ) : (
        <div className="tabellenrahmen">
          <table>
            <thead>
              <tr>
                <th>Fertigartikel</th>
                <th>zieht Material</th>
                <th>gültig</th>
                <th>heute</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {kopplungen.map((k) => {
                const imBearbeiten = bearbeitet !== null && bearbeitet.id === k.id
                // Ein älteres Backend liefert keine Id - dann lässt sich nur ansehen.
                const aenderbar = typeof k.id === 'number'
                return (
                  <tr key={aenderbar ? k.id : `${k.fertigartikel}-${k.material}`} className={k.gilt_heute === false ? 'inaktiv' : ''}>
                    <td>
                      {k.fertigartikel} {k.fertigartikel_bezeichnung}
                    </td>
                    <td>
                      {k.material} {k.material_bezeichnung}
                      {k.hergeleitet && <span className="hinweis"> (aus Regel)</span>}
                    </td>
                    <td>
                      {imBearbeiten ? (
                        <span className="leiste" style={{ marginBottom: 0 }}>
                          <label className="hinweis">
                            ab <input type="date" value={bearbeitet.ab} onChange={(e) => setBearbeitet({ ...bearbeitet, ab: e.target.value })} />
                          </label>
                          <label className="hinweis">
                            bis <input type="date" value={bearbeitet.bis} onChange={(e) => setBearbeitet({ ...bearbeitet, bis: e.target.value })} />
                          </label>
                        </span>
                      ) : (
                        zeitraumText(k)
                      )}
                    </td>
                    <td>
                      {k.gilt_heute === undefined ? '' : k.gilt_heute ? (
                        <span className="status gebucht">gilt</span>
                      ) : (
                        <span className="status verworfen">gilt heute nicht</span>
                      )}
                    </td>
                    <td>
                      {aenderbar && imBearbeiten && (
                        <>
                          <button className="haupt" disabled={arbeitet} onClick={aendern}>
                            Speichern
                          </button>{' '}
                          <button disabled={arbeitet} onClick={() => setBearbeitet(null)}>
                            Abbrechen
                          </button>
                        </>
                      )}
                      {aenderbar && !imBearbeiten && (
                        <>
                          <button
                            className="leise"
                            disabled={arbeitet}
                            onClick={() => setBearbeitet({ id: k.id, ab: k.gueltig_ab ?? '', bis: k.gueltig_bis ?? '' })}
                          >
                            Gültigkeit ändern
                          </button>{' '}
                          <button className="leise" disabled={arbeitet} onClick={() => loeschen(k)}>
                            Entfernen
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="leiste felder" style={{ marginTop: 8 }}>
        {artikel?.art !== 'fertigartikel' && (
          <label className="feld">
            <span className="hinweis">{artikel ? `Fertigartikel, der ${artikel.artikelnummer} zieht` : 'Fertigartikel'}</span>
            <ArtikelAuswahl artikel={fertigartikel} wert={neuFertig} onChange={setNeuFertig} leer="bitte wählen" />
          </label>
        )}
        {artikel?.art !== 'material' && (
          <label className="feld">
            <span className="hinweis">{artikel ? `Material, das ${artikel.artikelnummer} zieht` : 'Material'}</span>
            <ArtikelAuswahl artikel={materialien} wert={neuMaterial} onChange={setNeuMaterial} leer="bitte wählen" />
          </label>
        )}
        <label className="feld">
          <span className="hinweis">gültig ab (leer = schon immer)</span>
          <input type="date" value={neuAb} onChange={(e) => setNeuAb(e.target.value)} />
        </label>
        <label className="feld">
          <span className="hinweis">gültig bis (leer = weiter)</span>
          <input type="date" value={neuBis} onChange={(e) => setNeuBis(e.target.value)} />
        </label>
        <button className="haupt" disabled={arbeitet} onClick={anlegen}>
          Koppeln
        </button>
      </div>
      <p className="hinweis" style={{ marginBottom: 0 }}>
        Gerechnet wird nur im Zeitraum: außerhalb zieht der Fertigartikel das Material nicht. Eine Kopplung aus einer Regel
        kommt beim nächsten Anwenden der Regeln wieder - dauerhaft ändern dann über die Regel.
      </p>
    </div>
  )
}
